import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryConfig } from "../api";
import { IMediaMusicWithMeta } from "../models/music.models";
import { getToken } from "../models/users.models";
import {
  IMedia,
  IMediaVideoWithMeta,
  IWatchedMediaWithMeta,
  UpdateMediaData,
} from "../models/video.models";

const mediaConfig = {
  ...baseQueryConfig,
  baseUrl: baseQueryConfig.baseUrl + "/media",
  prepareHeaders: (headers: Headers) => {
    const session = getToken();
    if (session && session.token) {
      headers.set("Authorization", `Bearer ${session.token}`);
    }
    return headers;
  },
};

export const mediaApi = createApi({
  reducerPath: "mediaApi",
  tagTypes: ["Media"],
  baseQuery: fetchBaseQuery(mediaConfig),
  endpoints: (builder) => ({
    getMediaMusic: builder.query<
      IMediaMusicWithMeta,
      {
        page: number;
        limit: number;
        sortByDate?: string;
        searchTerm?: string;
        profileUid?: string;
        isFavourite?: boolean;
        isPublished?: string;
        sections?: string;
      }
    >({
      query: ({
        page,
        limit,
        sortByDate,
        searchTerm,
        profileUid,
        isFavourite,
        isPublished,
        sections,
      }) => ({
        url: "/search/music",
        params: {
          page,
          limit,
          sortByDate,
          searchTerm,
          profileUid,
          isFavourite,
          isPublished,
          sections,
        },
      }),
      providesTags: ["Media"],
    }),
    getMediaVideo: builder.query<
      IMediaVideoWithMeta,
      {
        page: number;
        limit: number;
        sortByDate: string;
        searchTerm?: string;
        profileUid?: string;
        isFavourite?: boolean;
        isPublished?: string;
        sections?: string;
        filterType?: string;
      }
    >({
      query: ({
        page,
        limit,
        sortByDate,
        searchTerm,
        profileUid,
        isFavourite,
        isPublished,
        sections,
        filterType,
      }) => ({
        url: "/search/video",
        params: {
          page,
          limit,
          sortByDate,
          searchTerm,
          profileUid,
          isFavourite,
          isPublished,
          sections,
          filterType,
        },
      }),
      providesTags: ["Media"],
    }),
    getRecommendedMedia: builder.query<
      IMedia[],
      {
        profileUid?: string;
        mediaType?: string;
      }
    >({
      query: ({ profileUid, mediaType }) => ({
        url: "/recommended",
        params: {
          profileUid,
          mediaType,
        },
      }),
      providesTags: ["Media"],
    }),
    getWatchedMedia: builder.query<
      IWatchedMediaWithMeta,
      {
        page: number;
        limit: number;
        profileUid: string;
        mediaType?: string;
        isSeen?: boolean;
      }
    >({
      query: ({ page, limit, profileUid, mediaType, isSeen }) => ({
        url: "/watched",
        params: {
          page,
          limit,
          profileUid,
          mediaType,
          isSeen,
        },
      }),
      providesTags: ["Media"],
    }),
    deleteMedia: builder.mutation<boolean, string>({
      query: (uid) => ({
        url: `/${uid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Media"],
    }),
    regenerateThumbnail: builder.mutation<void, { mediaUid: string }>({
      query: ({ mediaUid }) => ({
        url: `/regenerate-thumbnail`,
        method: "POST",
        body: { mediaUid },
      }),
      invalidatesTags: ["Media"],
    }),
    updateMedia: builder.mutation<void, UpdateMediaData>({
      query: (data) => ({
        url: "",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Media"],
    }),
  }),
});

export const {
  useGetMediaMusicQuery,
  useGetMediaVideoQuery,
  useGetRecommendedMediaQuery,
  useDeleteMediaMutation,
  useRegenerateThumbnailMutation,
  useUpdateMediaMutation,
  useGetWatchedMediaQuery,
} = mediaApi;
